import { useState, useCallback, useRef } from 'react';
import { lookupWord } from '../Services/dictionaryService';

const cache = new Map(); // simple in-memory cache per session

/**
 * Solo results' word lookup, backed by the same dictionary service as
 * multiplayer review (Merriam-Webster, then AI, then Wikipedia).
 *
 * Usage:
 *   const { lookup, result, loading, error } = useDictionary();
 *   lookup('axolotl', 'animal');
 */
export function useDictionary() {
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const latest = useRef(0);

  const lookup = useCallback(async (word, category) => {
    if (!word?.trim()) return;
    const key = `${word.trim().toLowerCase()}|${category || ''}`;
    const id = ++latest.current;

    setError(null);
    setResult(null);

    if (cache.has(key)) {
      setResult(cache.get(key));
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const found = await lookupWord(word, { category });
      const entry = {
        word: found.word,
        notFound: false,
        phonetic: found.phonetic || null,
        audioUrl: null,
        source: found.source,
        categoryMatch: found.categoryMatch,
        meanings: [{ partOfSpeech: found.partOfSpeech, definition: found.definition, example: found.example }],
      };
      cache.set(key, entry);
      if (id === latest.current) setResult(entry);
    } catch (err) {
      if (id !== latest.current) return;
      if (err.message === 'not_found') {
        const entry = { word: word.trim(), notFound: true };
        cache.set(key, entry);
        setResult(entry);
      } else {
        setError(true);
      }
    } finally {
      if (id === latest.current) setLoading(false);
    }
  }, []);

  const clear = useCallback(() => {
    latest.current++;
    setResult(null);
    setError(null);
    setLoading(false);
  }, []);

  return { lookup, result, loading, error, clear };
}
